import React from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import CapabilityCircle from 'components/modules/user/CapabilityCircle';
import formatMoney from 'utils/formatters/Money';
import { getShipmentDetailUrl, getLoad, getEquipment, } from 'utils/formatters/shipments';
import { useTranslation } from 'react-i18next';
import SignedUrlButton from './SignedUrlButton';
import { isArray } from 'lodash';
import { titleize } from 'underscore.string';
import { sluggify } from '../../../utils/localization/utils';
import { NO_DATA } from '../../../utils/constants';
const formatLocation = (val) => {
    if (!val)
        return '';
    if (typeof val === 'string')
        return val;
    const loc = isArray(val) ? val[0] : val;
    return (loc.name ||
        loc.street ||
        (loc.city && loc.state ? `${loc.city}, ${loc.state}` : '') ||
        loc.address ||
        '');
};
const VALUE_TYPES = {
    DATE: 'DATE',
    EQUIPMENT: 'EQUIPMENT',
    LINK: 'LINK',
    VERIFY_ICON: 'VERIFY_ICON',
    LOCATION: 'LOCATION',
    LOAD: 'LOAD',
    MONEY: 'MONEY',
    DEFAULT: 'DEFAULT',
};
const FORMATTERS = {
    DATE: (value) => moment(value).format('L'),
    EQUIPMENT: getEquipment,
    LINK: (valueId, displayValue = '') => {
        return (React.createElement(Link, { to: getShipmentDetailUrl(valueId), target: "_blank" }, displayValue || valueId));
    },
    LOAD: getLoad,
    LOCATION: formatLocation,
    MONEY: (value, currency) => formatMoney(value, currency),
    VERIFY_ICON: (verified, message) => {
        if (verified) {
            return React.createElement(CapabilityCircle, { checked: verified });
        }
        if (!message)
            return null;
        return React.createElement("span", { "data-testid": "span-f8428f44" }, message);
    },
    DEFAULT: (value) => value,
};
const getValue = (value, type, displayValue = '', currency) => {
    switch (type) {
        case VALUE_TYPES.DATE:
            return value ? FORMATTERS.DATE(value) : '';
        case VALUE_TYPES.EQUIPMENT:
            return value ? FORMATTERS.EQUIPMENT(value) : '';
        case VALUE_TYPES.LINK:
            return value ? FORMATTERS.LINK(value, displayValue) : '';
        case VALUE_TYPES.LOAD:
            return value ? FORMATTERS.LOAD(value) : '';
        case VALUE_TYPES.LOCATION:
            return value ? FORMATTERS.LOCATION(value) : '';
        case VALUE_TYPES.MONEY:
            return value
                ? FORMATTERS.MONEY(value, currency)
                : FORMATTERS.MONEY(0, currency);
        case VALUE_TYPES.VERIFY_ICON:
            return value ? FORMATTERS.VERIFY_ICON(value) : '';
        default:
            return FORMATTERS.DEFAULT(value);
    }
};
const JobPODColumn = ({ job }) => {
    if (job.pod === 'Missing POD' || !job.podUrl)
        return null;
    return React.createElement(SignedUrlButton, { documentKey: job.pod, className: "h-8 w-8" });
};
const ChargeMessage = ({ item }) => {
    var _a, _b;
    const { t } = useTranslation();
    const lineItem = item._lineItem;
    if (!lineItem)
        return null;
    const message = lineItem.type === 'customer_adjustment'
        ? titleize((_a = lineItem.message) !== null && _a !== void 0 ? _a : '')
        : t(sluggify((_b = lineItem.message) !== null && _b !== void 0 ? _b : ''));
    return React.createElement("span", null, message);
};
const SHORTHAUL_COLUMNS = [
    {
        title: 'CHARGE',
        titleize: true,
        field: 'charge',
        className: 'text-left min-w-[200px]',
        value(item) {
            return React.createElement(ChargeMessage, { item: item });
        },
    },
    {
        title: 'AMOUNT',
        field: 'amount',
        value(item) {
            const lineItem = item._lineItem;
            if (!lineItem)
                return '';
            return getValue(lineItem.value, VALUE_TYPES.MONEY, '', lineItem.currency);
        },
    },
    {
        title: 'DELIVER_BY',
        field: 'jobDeliverBy',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
    {
        title: 'SHIPMENT',
        field: 'shipment_id',
        className: 'whitespace-nowrap',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
        },
    },
    {
        title: 'POD_VERIFICATION',
        field: 'is_pod_valid',
        className: 'whitespace-nowrap',
        value(item) {
            const val = item[this.field];
            if (val == null)
                return '';
            return FORMATTERS.VERIFY_ICON(val);
        },
    },
    {
        title: 'ORIGIN',
        titleize: true,
        field: 'origin',
        className: 'min-w-[160px]',
        value(item) {
            return formatLocation(item[this.field]);
        },
    },
    {
        title: 'DESTINATION',
        titleize: true,
        field: 'destination',
        optional: true,
        className: 'min-w-[160px]',
        value(item) {
            return formatLocation(item[this.field]);
        },
    },
    {
        title: 'LOAD',
        field: 'loads',
        optional: true,
        className: 'min-w-[160px]',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOAD);
        },
    },
    {
        title: 'WEIGHT',
        field: 'weight',
        optional: true,
        className: 'text-right',
        value(item) {
            if (item.weight == null)
                return NO_DATA;
            const formatted = item.weight.toLocaleString();
            const unit = item.weight_units ? ` ${item.weight_units}` : '';
            return `${formatted}${unit}`;
        },
    },
    {
        title: 'DRIVER',
        titleize: true,
        count: 1,
        field: 'driverName',
        optional: true,
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'POD',
        field: 'pod',
        optional: true,
        value(item) {
            return React.createElement(JobPODColumn, { job: item });
        },
    },
    {
        title: 'CLOSED_DATE',
        field: 'jobClosed',
        className: 'whitespace-nowrap',
        optional: true,
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
];
const DRAYAGE_COLUMNS = [
    {
        title: 'CHARGE',
        titleize: true,
        field: 'charge',
        className: 'text-left min-w-[200px]',
        value(item) {
            return React.createElement(ChargeMessage, { item: item });
        },
    },
    {
        title: 'AMOUNT',
        field: 'amount',
        value(item) {
            const lineItem = item._lineItem;
            if (!lineItem)
                return '';
            return getValue(lineItem.value, VALUE_TYPES.MONEY, '', lineItem.currency);
        },
    },
    {
        title: 'DELIVER_BY',
        field: 'jobDeliverBy',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
    {
        title: 'SHIPMENT',
        field: 'shipment_id',
        className: 'whitespace-nowrap',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
        },
    },
    {
        title: 'POD_VERIFICATION',
        field: 'is_pod_valid',
        className: 'whitespace-nowrap',
        value(item) {
            const val = item[this.field];
            if (val == null)
                return '';
            return FORMATTERS.VERIFY_ICON(val);
        },
    },
    {
        title: 'ORIGIN',
        titleize: true,
        field: 'origin',
        className: 'min-w-[160px]',
        value(item) {
            return formatLocation(item[this.field]);
        },
    },
    {
        title: 'DESTINATION',
        titleize: true,
        field: 'destination',
        optional: true,
        className: 'min-w-[160px]',
        value(item) {
            return formatLocation(item[this.field]);
        },
    },
    {
        title: 'EQUIPMENT',
        field: 'equipments',
        optional: true,
        className: 'min-w-[160px]',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.EQUIPMENT);
        },
    },
    {
        title: 'WEIGHT',
        field: 'weight',
        optional: true,
        className: 'text-right',
        value(item) {
            if (item.weight == null)
                return NO_DATA;
            const formatted = item.weight.toLocaleString();
            const unit = item.weight_units ? ` ${item.weight_units}` : '';
            return `${formatted}${unit}`;
        },
    },
    {
        title: 'DRIVER',
        titleize: true,
        count: 1,
        field: 'driverName',
        optional: true,
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'POD',
        field: 'pod',
        optional: true,
        value(item) {
            return React.createElement(JobPODColumn, { job: item });
        },
    },
    {
        title: 'CLOSED_DATE',
        field: 'jobClosed',
        className: 'whitespace-nowrap',
        optional: true,
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
];
const COLUMNS = {
    shorthaul: SHORTHAUL_COLUMNS,
    drayage: DRAYAGE_COLUMNS,
};
export default COLUMNS;
